/* eslint-disable react/jsx-props-no-spreading,react/prop-types */
import * as React from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import {
  InputBaseContainer,
  InputSelectWithLabel,
  InputDatePickerWithLabel,
  ErrorMessages,
  ButtonWithLoader,
} from '../molecules';
import { FormServerError } from '../types';
import { DEFAULT_DATE_FORMAT } from '../constants';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import {
  USER_TYPES,
  USER_TYPES_VALUES,
} from '../constants/forms';
import {
  MessagesType,
  MessageBranchType,
  getMessageBranches,
} from '../api/messages';
import { getUsers } from '../api/users';
import RichTextEditorWithLabel, {
  RICH_TOOLBAR,
} from '../molecules/RichTextEditorWithLabel';
import { htmlToPlainText } from '../utils/sanitizeHtml';
import formatDate from '../utils/formatDate';

// Django DateField wants ISO; DEFAULT_DATE_FORMAT is for display only.
const API_DATE_FORMAT = 'yyyy-MM-dd';

// Midnight local time, so a "Show until" of *today* still compares as valid.
// react-datepicker hands back a Date carrying a time component, so comparing
// against `new Date()` would reject today for the rest of the day.
const startOfToday = () => {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  return midnight;
};

// Build the Date from its parts so it lands on local midnight, matching what
// the picker hands back. Letting Date() parse the string would read an ISO date
// as UTC and shift it a day west of Greenwich - this app runs in
// America/New_York.
//
// Both shapes are accepted because the API speaks MM/DD/YYYY: DRF's global
// DATE_FORMAT is '%m/%d/%Y' (dashboard_project/settings.py). Assuming ISO here
// produced an Invalid Date, which left the edit modal's pickers blank and made
// formatDate() throw on save.
const parseApiDate = (value?: string | null) => {
  if (!value) return null;
  const text = String(value);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const usa = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usa) return new Date(+usa[3], +usa[1] - 1, +usa[2]);
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// The Client tab has ~6,500 users. Loading them all (the old
// GET_ALL_QUERY_PARAMS limit of 100,000,000) hung the modal outright.
//
// Employees and inspectors number in the dozens, so they still preload
// complete. Clients are search-gated instead - the same 300 ms debounce and
// 2-character gate ContactSearchList uses for exactly this reason.
const PEOPLE_PAGE_SIZE = 50;
const PEOPLE_SEARCH_DEBOUNCE_MS = 300;
const PEOPLE_SEARCH_MIN_CHARS = 2;

const labels = {
  text: 'Message',
  dateFrom: 'Show from (optional)',
  dateTo: 'Show until (optional)',
  franchise: 'Franchise',
  userRole: 'User type',
  addresses: 'Addresses',
};

/**
 * @param grandfatheredDateTo the end date this message was loaded with, or null
 *   when creating. An already-closed window must stay saveable: an admin fixing
 *   a typo on an old message shouldn't be forced to re-date it. Only a *newly
 *   chosen* end date has to be today or later.
 */
const buildSchema = (grandfatheredDateTo: Date | null) =>
  yup.object().shape({
    // Quill returns "<p><br></p>" for an empty editor, which is a non-empty
    // string - so required() alone would let a blank message through.
    text: yup
      .string()
      .required()
      .test(
        'not-blank',
        'Message is a required field',
        value => htmlToPlainText(value).length > 0,
      )
      .label(labels.text),
    franchise: yup.string(),
    userRole: yup.number(),
    // `audience` and `addresses` are driven by Controllers and validated by
    // hand below. They stay in the schema so the resolver's cast keeps them on
    // the submitted object, and so setError() has a registered field to attach
    // a server-side error to.
    audience: yup.mixed(),
    addresses: yup.array(),
    // Optional display window for holiday / seasonal notices. Left empty, the
    // message behaves as before: it shows on the day it was sent.
    dateFrom: yup.mixed().nullable(),
    dateTo: yup
      .mixed()
      .nullable()
      .test(
        'after-start',
        'The end date cannot be earlier than the start date.',
        // eslint-disable-next-line func-names
        function (value) {
          const from = this.parent?.dateFrom;
          if (!from || !value) return true;
          return new Date(value) >= new Date(from);
        },
      )
      .test(
        'not-past',
        'The end date must be today or later.',
        value => {
          if (!value) return true;
          const picked = new Date(value);
          if (
            grandfatheredDateTo &&
            picked.getTime() === grandfatheredDateTo.getTime()
          ) {
            return true;
          }
          return picked >= startOfToday();
        },
      ),
  });

/**
 * A row of the Addresses dropdown: everyone, one branch, or "pick people".
 *
 * Shaped as react-select's default OptionType ({ label, value }) so
 * getOptionLabel / getOptionValue can be left off. `value` is a composite key
 * because Franchise and User primary keys are separate spaces - a raw `id`
 * would collide branch 1 with user 1.
 */
interface AudienceOption {
  kind: 'all' | 'branch' | 'specific';
  value: string;
  label: string;
  branchId?: number;
  branchName?: string;
}

/** Form-only shape: `audience` drives the UI but never reaches the API. */
type MessagesFormValues = MessagesType & {
  audience?: AudienceOption;
  // Date objects while editing; serialised to ISO on submit.
  dateFrom?: any;
  dateTo?: any;
};

interface PersonOption {
  value: string;
  label: string;
  userId: number;
  branchNames: string;
}

const buildBranchOption = (
  branch: MessageBranchType,
): AudienceOption => ({
  kind: 'branch',
  value: `branch-${branch.id}`,
  label: `${branch.name} Branch Only`,
  branchId: branch.id,
  branchName: branch.name,
});

const buildPersonOption = (user: any): PersonOption => {
  // Never `${firstName} ${lastName}` - the API returns undefined for either.
  const name = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ');

  return {
    value: `user-${user.id}`,
    label: name || user?.email || `User #${user.id}`,
    userId: user.id,
    branchNames: user?.franchiseNames || '',
  };
};

const formatGroupLabel = (group: any) => (
  <GroupHeading>
    <GroupHeadingText>{group.label}</GroupHeadingText>
    {group?.options?.length > 1 && (
      <GroupCount>{group.options.length}</GroupCount>
    )}
  </GroupHeading>
);

const buildOptionFormatter = (roleWord: string) => (
  option: any,
  meta: any,
) => {
  const { context } = meta || {};
  const { kind, label, branchNames } = option || {};

  // The closed control and the chips stay one line; only the open menu gets
  // the muted second row.
  if (context === 'value') return label;

  let secondary = '';
  if (kind === 'all') {
    secondary = `Every ${roleWord}, every branch`;
  } else if (kind === 'branch') {
    secondary = `Only ${roleWord}s at this branch`;
  } else if (kind === 'specific') {
    secondary = 'Pick them one by one';
  } else {
    secondary = branchNames;
  }

  if (!secondary) return label;

  return (
    <OptionRow>
      <OptionPrimary>{label}</OptionPrimary>
      <OptionSecondary>{secondary}</OptionSecondary>
    </OptionRow>
  );
};

const MessagesForm = ({
  onSubmit,
  modalError,
  selectedUserRole,
  initialValues,
  addButtonText = 'Send',
  updateButtonText = 'Save',
  initialErrors,
  isEditing = false,
  audienceLabel = '',
}: Props) => {
  const roleLabel =
    USER_TYPES.find(item => item.value === selectedUserRole)
      ?.label || 'User';
  const roleWord = roleLabel.toLowerCase();

  // Branch scoping is offered on the Employee and Inspector tabs only.
  const supportsBranchScoping =
    selectedUserRole === USER_TYPES_VALUES.EMPLOYEE ||
    selectedUserRole === USER_TYPES_VALUES.INSPECTOR;

  const allOption = React.useMemo<AudienceOption>(
    () => ({
      kind: 'all',
      value: 'all',
      label: `All ${roleLabel}s`,
    }),
    [roleLabel],
  );

  const specificOption = React.useMemo<AudienceOption>(
    () => ({
      kind: 'specific',
      value: 'specific',
      label: `Specific ${roleWord}s…`,
    }),
    [roleWord],
  );

  const initialDateFrom = React.useMemo(
    () => parseApiDate(initialValues?.dateFrom as any),
    [initialValues?.dateFrom],
  );
  const initialDateTo = React.useMemo(
    () => parseApiDate(initialValues?.dateTo as any),
    [initialValues?.dateTo],
  );
  const schema = React.useMemo(
    () => buildSchema(initialDateTo),
    [initialDateTo],
  );

  const {
    register,
    handleSubmit,
    control,
    errors,
    setError,
    setValue,
    watch,
    formState,
  } = useForm<MessagesFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      userRole: selectedUserRole,
      audience: allOption,
      addresses: [],
      text: initialValues?.text || '',
      dateFrom: initialDateFrom,
      dateTo: initialDateTo,
    } as any,
  });

  // "Show until" can never be earlier than today, nor than "Show from" - a
  // window that has already closed would never show the message. The stored
  // value on an existing message is exempt (see buildSchema), so an expired
  // notice stays editable.
  const dateFromValue = watch('dateFrom') as any;
  const dateToMinDate = React.useMemo(() => {
    const floor = startOfToday();
    const from = dateFromValue ? new Date(dateFromValue) : null;
    return from && from > floor ? from : floor;
  }, [dateFromValue]);

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [branches, setBranches] = React.useState<
    AudienceOption[]
  >([]);
  const [people, setPeople] = React.useState<PersonOption[]>(
    [],
  );
  const [peopleTotal, setPeopleTotal] = React.useState(0);
  const [peopleLoading, setPeopleLoading] = React.useState(
    false,
  );
  // Discards out-of-order responses so a slow early query can't overwrite the
  // results of a later one.
  const peopleQueryRef = React.useRef('');
  const debounceRef = React.useRef(null);
  // yup conditional validation is unreliable with resolvers v0.1.1, so the
  // "pick at least one person" rule is enforced by hand.
  const [peopleError, setPeopleError] = React.useState('');

  // Too many clients to list; they must be searched for.
  const peopleAreSearchOnly =
    selectedUserRole === USER_TYPES_VALUES.CLIENT;

  const fetchPeople = (query: string) => {
    peopleQueryRef.current = query;

    // Nothing to preload for a search-only role - wait for a real query.
    if (
      peopleAreSearchOnly &&
      query.length < PEOPLE_SEARCH_MIN_CHARS
    ) {
      setPeople([]);
      setPeopleTotal(0);
      setPeopleLoading(false);
      return Promise.resolve();
    }

    setPeopleLoading(true);

    const params: any = {
      isActive: true,
      isDeleted: false,
      offset: 0,
      limit: PEOPLE_PAGE_SIZE,
      userRole: selectedUserRole,
    };
    if (query) params.search = query;

    return getUsers(params)
      .then(response => {
        if (peopleQueryRef.current !== query) return;
        setPeople(
          (response.data.results || []).map(
            buildPersonOption,
          ),
        );
        setPeopleTotal(response.data.count || 0);
      })
      .catch(() => {})
      .finally(() => {
        if (peopleQueryRef.current === query) {
          setPeopleLoading(false);
        }
      });
  };

  const handlePeopleInputChange = (
    value: string,
    meta: any,
  ) => {
    // Ignore the blur / menu-close callbacks react-select also fires here.
    if (meta?.action !== 'input-change') return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(
      () => fetchPeople(value.trim()),
      PEOPLE_SEARCH_DEBOUNCE_MS,
    );
  };

  React.useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    // Editing is text-only, so the audience pickers never render and their
    // (expensive) lookups are skipped.
    if (isEditing) return;

    setIsLoading(true);

    Promise.all([getMessageBranches(), fetchPeople('')])
      .then(([branchesResponse]) => {
        // Every live branch, not just the admin's own - a Branch Admin may
        // address any of them from here.
        const rows = branchesResponse.data || [];

        setValue(
          'franchise',
          rows.map(item => item.id).filter(Boolean),
        );
        setBranches(rows.map(buildBranchOption));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<MessagesFormValues>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  // A single-branch deployment gains nothing from "Naples Branch Only" - it is
  // the same audience as "All Employees".
  const showBranches =
    supportsBranchScoping && branches.length > 1;

  const audienceOptions = React.useMemo<any[]>(() => {
    const options: any[] = [allOption];

    if (showBranches) {
      options.push({
        label: 'By branch',
        options: branches,
      });
    }

    options.push(specificOption);
    return options;
  }, [allOption, showBranches, branches, specificOption]);

  const audience = watch('audience');
  const selectedPeople = (watch('addresses') ||
    []) as any as PersonOption[];
  const isSpecific = audience?.kind === 'specific';

  const audienceHint = React.useMemo(() => {
    if (!audience) return '';
    if (audience.kind === 'branch') {
      return `Goes to every ${roleWord} at ${audience.branchName}.`;
    }
    if (audience.kind === 'specific') {
      return `Choose who receives this message below.`;
    }
    return branches.length > 1
      ? `Goes to every ${roleWord} at all ${branches.length} branches.`
      : `Goes to every ${roleWord}.`;
  }, [audience, branches, roleWord]);

  const formatOption = React.useMemo(
    () => buildOptionFormatter(roleWord),
    [roleWord],
  );

  // Runs before handleSubmit so a block never strands formState.isSubmitting.
  const validatePeople = () => {
    if (isEditing) return true;
    if (!isSpecific) return true;
    if (selectedPeople.length) return true;
    setPeopleError(
      `Select at least one ${roleWord}, or choose a different audience.`,
    );
    return false;
  };

  const middleware = (
    submitFn: (data: MessagesType) => void,
  ) => (data: MessagesFormValues) => {
    // `audience` is form state only - keep it out of the request body.
    const { audience: picked, ...payload } = data;

    if (isEditing) {
      // Audience is immutable server-side too (MessageEditSerializer); the
      // display window stays adjustable.
      submitFn({
        id: initialValues?.id,
        text: data.text,
        dateFrom: data.dateFrom
          ? formatDate(data.dateFrom, API_DATE_FORMAT)
          : null,
        dateTo: data.dateTo
          ? formatDate(data.dateTo, API_DATE_FORMAT)
          : null,
      } as any);
      return;
    }

    const chosenPeople = ((data.addresses as any) ||
      []) as PersonOption[];

    const everyBranch = branches
      .map(item => item.branchId)
      .filter(Boolean);

    const asDate = (value: any) =>
      value ? formatDate(value, API_DATE_FORMAT) : null;

    submitFn({
      ...payload,
      dateFrom: asDate(data.dateFrom),
      dateTo: asDate(data.dateTo),
      franchise:
        picked?.kind === 'branch'
          ? [picked.branchId]
          : everyBranch,
      addresses:
        picked?.kind === 'specific'
          ? chosenPeople.map(item => item.userId)
          : [],
    });
  };

  // Extracted rather than inlined: the editing/creating split plus the
  // conditional people picker nests four levels deep inside the form JSX.
  const readOnlyAudience = (
    <Field>
      <ReadOnlyLabel>{labels.addresses}</ReadOnlyLabel>
      <ReadOnlyValue>{audienceLabel || '—'}</ReadOnlyValue>
      <FieldHint>
        The audience is fixed once a message is sent. Delete
        it and send a new one to reach someone else.
      </FieldHint>
    </Field>
  );

  const audiencePicker = (
    <Field>
      <InputBaseContainer>
        <Controller
          name="audience"
          control={control}
          render={props => (
            <InputSelectWithLabel
              placeholder=""
              label={labels.addresses}
              isLoading={isLoading}
              isDisabled={isLoading}
              isSearchable={false}
              isClearable={false}
              isRequired
              options={audienceOptions}
              formatGroupLabel={formatGroupLabel}
              formatOptionLabel={formatOption}
              {...props}
              onChange={(value: any) => {
                setPeopleError('');
                // Leaving "Specific ..." drops any people already
                // picked, so a stale list can never ride along on a
                // broadcast.
                if (value?.kind !== 'specific') {
                  setValue('addresses', []);
                }
                props.onChange(value);
              }}
            />
          )}
        />
      </InputBaseContainer>
      {audienceHint && <FieldHint>{audienceHint}</FieldHint>}
    </Field>
  );

  const peoplePicker = (
    <Field>
      <InputBaseContainer>
        <Controller
          name="addresses"
          control={control}
          render={props => (
            <InputSelectWithLabel
              isMulti
              placeholder={`Search ${roleWord}s…`}
              label={`${roleLabel}s`}
              error={
                peopleError
                  ? { message: peopleError }
                  : undefined
              }
              isLoading={isLoading || peopleLoading}
              isDisabled={isLoading}
              isRequired
              options={people}
              formatOptionLabel={formatOption}
              // Matching happens on the server (name, email,
              // company), so react-select must not also filter - it
              // would hide a company-name match whose label is a
              // person's name.
              filterOption={null}
              onInputChange={handlePeopleInputChange}
              closeMenuOnSelect={false}
              noOptionsMessage={({ inputValue }) => {
                if (peopleLoading) return 'Searching…';
                if (
                  peopleAreSearchOnly &&
                  (inputValue || '').trim().length <
                    PEOPLE_SEARCH_MIN_CHARS
                ) {
                  return `Type at least ${PEOPLE_SEARCH_MIN_CHARS} characters to search ${roleWord}s.`;
                }
                return `No ${roleWord}s match that search.`;
              }}
              {...props}
              onChange={(value: any) => {
                setPeopleError('');
                props.onChange(value || []);
              }}
            />
          )}
        />
      </InputBaseContainer>
      {!peopleError && (
        <FieldHint>
          {selectedPeople.length
            ? `${selectedPeople.length} ${roleWord}${
                selectedPeople.length > 1 ? 's' : ''
              } selected.`
            : 'No one selected yet.'}
          {peopleAreSearchOnly && !people.length
            ? ` Start typing to search ${roleWord}s.`
            : peopleTotal > people.length &&
              ` Showing ${people.length} of ${peopleTotal} matches — refine your search to narrow it.`}
        </FieldHint>
      )}
    </Field>
  );

  const audienceSection = isEditing ? (
    readOnlyAudience
  ) : (
    <>
      {audiencePicker}
      {isSpecific && peoplePicker}
    </>
  );

  return (
    <Container disabled={isSubmitting && !modalError}>
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit))}
      >
        {/*
          Each field gets its OWN InputBaseContainer with a single child.
          Handing it an array lays the children out as side-by-side flex
          columns (flex: 1 1 0), which is what threw the hint and the summary
          card up next to the select instead of under it.
        */}
        {audienceSection}

        <Field>
          <InputBaseContainer>
            <Controller
              name="dateFrom"
              defaultValue={null}
              valueName="selected"
              onChange={([selected]) => selected}
              control={control}
              render={props => (
                <InputDatePickerWithLabel
                  label={labels.dateFrom}
                  error={errors.dateFrom}
                  dateFormat={DEFAULT_DATE_FORMAT}
                  isClearable
                  placeholderText="Any day"
                  selected={props.value}
                  {...props}
                />
              )}
            />
            <Controller
              name="dateTo"
              defaultValue={null}
              valueName="selected"
              onChange={([selected]) => selected}
              control={control}
              render={props => (
                <InputDatePickerWithLabel
                  label={labels.dateTo}
                  error={errors.dateTo}
                  dateFormat={DEFAULT_DATE_FORMAT}
                  isClearable
                  placeholderText="Any day"
                  minDate={dateToMinDate}
                  selected={props.value}
                  {...props}
                />
              )}
            />
          </InputBaseContainer>
          <FieldHint>
            Leave both empty to show it today only. Set a
            range for a holiday or seasonal notice and it
            shows every day inside that range instead.
            &ldquo;Show until&rdquo; must be today or later.
          </FieldHint>
        </Field>

        <Field>
          <InputBaseContainer>
            <Controller
              name="text"
              control={control}
              render={props => (
                <RichTextEditorWithLabel
                  label={labels.text}
                  value={props.value || ''}
                  onChange={props.onChange}
                  error={errors.text}
                  isRequired={isYupFieldRequired(
                    'text',
                    schema,
                  )}
                  toolbar={RICH_TOOLBAR}
                  placeholder="Write your announcement…"
                  tall
                />
              )}
            />
          </InputBaseContainer>
        </Field>

        <input
          type="hidden"
          name="userRole"
          ref={register}
        />
        {/*
          Stays registered even though middleware computes the outgoing value.
          react-hook-form v6's handleSubmit refuses to run onValid while any
          error key is missing from fieldsRef, so a server-side `franchise`
          error on an unregistered field would strand isSubmitting=true and
          kill the form for good.
        */}
        <input
          type="hidden"
          name="franchise"
          ref={register}
        />

        {errors?.franchise && (
          <ErrorMessages error={errors.franchise} />
        )}

        <Actions>
          <ButtonWithLoader
            disabled={isSubmitting && !modalError}
            loading={isSubmitting && !modalError}
            type="submit"
            onClick={(
              event: React.MouseEvent<HTMLButtonElement>,
            ) => {
              if (!validatePeople()) event.preventDefault();
            }}
          >
            {initialValues ? updateButtonText : addButtonText}
          </ButtonWithLoader>
        </Actions>
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: left;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 0;
  }
`;

const Field = styled.div`
  width: 100%;

  & + & {
    margin-top: 12px;
  }
`;

const ReadOnlyLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 6px;
  text-align: left;
`;

const ReadOnlyValue = styled.div`
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
  color: var(--neutral-700);
  font-size: 0.875rem;
  box-sizing: border-box;
  text-align: left;
`;

const FieldHint = styled.p`
  margin: 6px 0 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--neutral-500);
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
`;

const GroupHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
`;

const GroupHeadingText = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const GroupCount = styled.span`
  flex-shrink: 0;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid var(--primary-100);
  background: var(--primary-50);
  color: var(--primary-700);
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
`;

const OptionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  /* clears the tick pseudo-element on selected rows */
  padding-right: 22px;
  text-align: left;
`;

const OptionPrimary = styled.span`
  font-size: 0.875rem;
  line-height: 1.3;
`;

const OptionSecondary = styled.span`
  font-size: 0.75rem;
  line-height: 1.3;
  /* selected rows paint primary-600; a fixed grey would go unreadable there */
  color: currentColor;
  opacity: 0.65;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface Props {
  addButtonText?: string;
  selectedUserRole: number;
  /** Text-only edit of an already-sent message. */
  isEditing?: boolean;
  /** Read-only summary of the audience shown while editing. */
  audienceLabel?: string;
  updateButtonText?: string;
  initialValues?: MessagesType;
  onSubmit: (data: MessagesType) => void;
  initialErrors?: FormServerError<MessagesType>;
  modalError: boolean;
}

export default MessagesForm;
